import { PROCESSING_RECIPE_OPERATIONS } from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";

const RecipeInputSchema = z.object({
  productV3Key: z.string(),
  position: z.number().int().nonnegative(),
  quantityPerBatch: z.number().positive(),
  scrapRatio: z.number().nonnegative(),
});

const RecipeSchema = z.object({
  id: z.string().uuid(),
  productV3Key: z.string(),
  operation: z.enum(PROCESSING_RECIPE_OPERATIONS),
  capabilityTag: z.string(),
  processingIncrement: z.number().nonnegative(),
  minUsableOffcut: z.number().nonnegative(),
  kerfQuantity: z.number().nonnegative(),
  outputQuantity: z.number().positive(),
  conversionCostPerBatch: z.number().nonnegative(),
  leadTimeDays: z.number().int().nonnegative(),
  yieldRatio: z.number().positive().max(1),
  isActive: z.boolean(),
  warehouseIds: z.array(z.string().uuid()),
  inputs: z.array(RecipeInputSchema),
});

export const processingRecipesContracts = {
  list: oc
    .input(z.object({ productV3Key: z.string().optional() }))
    .output(z.object({ recipes: z.array(RecipeSchema) }))
    .route({
      method: "GET",
      summary: "List product processing recipes",
      description:
        "Returns cutting/processing recipes (capability, processing increment, minimum usable offcut, kerf, active flag, warehouses), optionally for one SKU.",
    }),
  upsert: oc
    .input(
      z.object({
        id: z.string().uuid().optional(),
        productV3Key: z.string().min(1),
        capabilityTag: z.string().min(1).optional(),
        processingIncrement: z.number().nonnegative().optional(),
        minUsableOffcut: z.number().nonnegative(),
        kerfQuantity: z.number().nonnegative().optional(),
        isActive: z.boolean().optional(),
        warehouseIds: z.array(z.string().uuid()),
      }),
    )
    .output(z.object({ recipe: RecipeSchema }))
    .route({
      method: "POST",
      summary: "Create or update a processing recipe",
      description:
        "Saves a product's cutting/processing recipe: increment, minimum usable offcut, kerf, capability, active flag and the warehouses where it applies.",
    }),
} as const;
